/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Normalize API createdDate (UTC ISO-8601 or epoch ms) to epoch ms.
 */
const normalizeCreatedDate = (value) => {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const asNumber = Number(trimmed);
    if (Number.isFinite(asNumber) && String(asNumber) === trimmed) {
      return asNumber;
    }
    const parsed = Date.parse(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const normalizeUpdatedDate = (value) => {
  if (value == null || value === '') {
    return null;
  }
  return String(value);
};

const isStrictBase64 = (value) => {
  if (value == null || typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) return false;

  const normalized = trimmed.replace(/-/g, '+').replace(/_/g, '/');
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(normalized)) return false;
  if (normalized.length % 4 !== 0) return false;

  // Short unpadded strings (e.g. "devh", "test") are valid base64 alphabet but plain text.
  if (normalized.length < 8 && !normalized.includes('=')) return false;

  try {
    return window.btoa(window.atob(normalized)) === normalized;
  } catch {
    return false;
  }
};

export const decodePromptContent = (value) => {
  if (value == null || value === '') return '';
  if (typeof value !== 'string') return String(value);
  if (!isStrictBase64(value)) return value;

  try {
    const base64 = value.trim().replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = window.atob(base64 + padding);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
  } catch {
    return value;
  }
};

const getMessageText = (message) => {
  if (!message) return '';
  const content = decodePromptContent(message.content);
  if (content) return content;
  return decodePromptContent(message.context);
};

const normalizeMessageList = (messages) => {
  if (!messages) return [];
  if (Array.isArray(messages)) return messages;
  if (typeof messages === 'object') return [messages];
  return [];
};

export const getSystemPromptFromDocument = (doc) => {
  const start = normalizeMessageList(doc?.promptStart);
  const systemMsg = start.find((m) => m?.role === 'system');
  if (systemMsg) return getMessageText(systemMsg);
  return getMessageText(start[0]);
};

export const getUserPromptFromDocument = (doc) => {
  const end = normalizeMessageList(doc?.promptEnd);
  const userMsg = end.find((m) => m?.role === 'user');
  if (userMsg) return getMessageText(userMsg);
  return getMessageText(end[end.length - 1]);
};

const resolveIsAgenticFlag = (value, fallback = false) => {
  if (value === true || value === false) {
    return value;
  }
  if (fallback === true || fallback === false) {
    return fallback;
  }
  return false;
};
const mapPromptMessages = (messages) =>
  normalizeMessageList(messages).map((message) => ({
    ...message,
    content: decodePromptContent(message?.content),
    ...(message?.context != null ? { context: decodePromptContent(message.context) } : {})
  }));

export const mapResolvedDocument = (data) => {
  if (!data) return null;
  return {
    id: data.id || data.promptRef || null,
    promptRef: data.promptRef || data.id || null,
    version: data.promptVersionRef || data.version || null,
    promptVersionRef: data.promptVersionRef || data.version || null,
    status: data.status || null,
    name: data.name || null,
    comments: data.comments || null,
    description: data.description || null,
    isAgentic: resolveIsAgenticFlag(data.isAgentic),
    previousVersion: data.previousVersion || null,
    // promptStart: Array.isArray(data.promptStart) ? data.promptStart : [],
    // promptEnd: Array.isArray(data.promptEnd) ? data.promptEnd : [],
    promptStart: mapPromptMessages(data.promptStart),
    promptEnd: mapPromptMessages(data.promptEnd),
    createdDate: normalizeCreatedDate(data.createdDate),
    updatedDate: normalizeUpdatedDate(data.updatedDate),
    modifiedBy: data.modifiedBy || null
  };
};

/** Merge a sparse API patch (create/update) onto an existing resolved prompt snapshot. */
export const mergeResolvedDocument = (existing, patch) => {
  if (!patch && !existing) {
    return null;
  }
  const base = mapResolvedDocument(existing) || {};
  const merged = {
    ...base,
    ...patch,
    id: patch?.id || patch?.promptRef || base.id,
    promptRef: patch?.promptRef || patch?.id || base.promptRef,
    version: patch?.version ?? patch?.promptVersionRef ?? base.version,
    promptVersionRef: patch?.promptVersionRef ?? patch?.version ?? base.promptVersionRef,
    name: patch?.name ?? base.name,
    comments: patch?.comments ?? base.comments,
    description: patch?.description ?? base.description,
    previousVersion: patch?.previousVersion ?? base.previousVersion,
    promptStart: Array.isArray(patch?.promptStart) ? patch.promptStart : base.promptStart,
    promptEnd: Array.isArray(patch?.promptEnd) ? patch.promptEnd : base.promptEnd,
    isAgentic: resolveIsAgenticFlag(patch?.isAgentic, base.isAgentic)
  };
  return mapResolvedDocument(merged);
};

/** True when hydrated resolvedPrompt belongs to the node's promptRef + promptVersionRef (alias-safe). */
export const resolvedPromptMatchesRefs = (promptRef, promptVersionRef, resolved) => {
  if (!resolved || promptRef == null || promptRef === '' || promptVersionRef == null || promptVersionRef === '') {
    return false;
  }
  const resolvedPromptRef = resolved.promptRef || resolved.id;
  const resolvedVersionRef = resolved.promptVersionRef || resolved.version;
  return String(resolvedPromptRef) === String(promptRef) && String(resolvedVersionRef) === String(promptVersionRef);
};

export const findVersionDocument = (versions, versionId, promptRef = null) => {
  if (!versionId || !Array.isArray(versions)) return null;
  return (
    versions.find((v) => {
      const rowVersion = v.promptVersionRef || v.version;
      if (String(rowVersion) !== String(versionId)) return false;
      if (!promptRef) return true;
      const rowPromptRef = v.promptRef || v.id;
      return String(rowPromptRef) === String(promptRef);
    }) || null
  );
};
